import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Plus, X, Pencil, Trash2, Download } from 'lucide-react';
import { NairaInput } from '../../lib/nairaInput';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

// Categories Sales Admin can record (everything EXCEPT land_acquisition + construction)
const ALLOWED_CATEGORIES = {
  marketing: 'Marketing',
  salaries: 'Salaries',
  legal_documentation: 'Legal/Documentation',
  infrastructure: 'Infrastructure',
  office_expenses: 'Office Expenses',
  utilities: 'Utilities',
  commission_payouts: 'Commission Payouts',
  miscellaneous: 'Miscellaneous',
};

const categoryColors = {
  marketing: 'bg-purple-100 text-purple-800',
  salaries: 'bg-blue-100 text-blue-800',
  legal_documentation: 'bg-slate-100 text-slate-800',
  infrastructure: 'bg-cyan-100 text-cyan-800',
  office_expenses: 'bg-amber-100 text-amber-800',
  utilities: 'bg-teal-100 text-teal-800',
  commission_payouts: 'bg-rose-100 text-rose-800',
  miscellaneous: 'bg-neutral-100 text-neutral-800',
};

const methodLabels = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', pos: 'POS', online: 'Online' };

const formatNaira = (amount) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);

const emptyForm = {
  amount: '',
  category: 'office_expenses',
  expense_date: new Date().toISOString().split('T')[0],
  description: '',
  payment_method: 'bank_transfer',
  vendor: '',
  reference: '',
};

const BOOfficeExpenses = () => {
  const { role, user } = useBOAuth();
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [categoryBreakdown, setCategoryBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirm, setConfirm] = useState({ open: false, expense: null });
  const formRef = useRef(null);

  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [showForm]);

  const fetchExpenses = async () => {
    try {
      const params = { scope: 'office' };
      if (filterCategory) params.category = filterCategory;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await boAPI.getExpenses(params);
      setExpenses(res.data.expenses || []);
      setTotalAmount(res.data.total_amount || 0);
      setCategoryBreakdown(res.data.category_breakdown || {});
    } catch (e) {
      toast.error('Failed to load office expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterCategory, dateFrom, dateTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.description.trim()) { toast.error('Please describe what was spent on'); return; }
    if (!form.expense_date) { toast.error('Date is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), expense_scope: 'office' };
      if (editExpense) {
        await boAPI.updateExpense(editExpense.id, payload);
        toast.success('Office expense updated');
      } else {
        await boAPI.createExpense(payload);
        toast.success('Office expense recorded');
      }
      setShowForm(false);
      setEditExpense(null);
      setForm({ ...emptyForm });
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (exp) => {
    setEditExpense(exp);
    setForm({
      amount: exp.amount || '',
      category: ALLOWED_CATEGORIES[exp.category] ? exp.category : 'office_expenses',
      expense_date: exp.expense_date || '',
      description: exp.description || '',
      payment_method: exp.payment_method || 'bank_transfer',
      vendor: exp.vendor || '',
      reference: exp.reference || '',
    });
    setShowForm(true);
  };

  const exportCSV = async () => {
    try {
      const params = { scope: 'office' };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await boAPI.exportExpensesCSV(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `medichi_office_expenses_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export CSV');
    }
  };

  const canEdit = (exp) => {
    if (role === 'owner') return true;
    return exp.recorded_by === user?.id;
  };

  if (loading) {
    return (
      <BOLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" />
        </div>
      </BOLayout>
    );
  }

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-office-expenses-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Office Expenses</h1>
            <p className="text-neutral-500 mt-1">
              {expenses.length} office expense{expenses.length !== 1 ? 's' : ''} recorded
              {role === 'sales_admin' && <span className="ml-1">— excludes Land Acquisition &amp; Construction</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={exportCSV}
              variant="outline"
              className="rounded-xl"
              data-testid="export-office-expenses-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button
              onClick={() => { setEditExpense(null); setForm({ ...emptyForm }); setShowForm(true); }}
              className="rounded-xl bg-primary-900 hover:bg-primary-800"
              data-testid="add-office-expense-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Record Office Expense
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-neutral-border shadow-card sm:col-span-2 lg:col-span-1">
            <CardContent className="p-4">
              <p className="text-xs text-neutral-500">Total Office Expenses</p>
              <p className="text-2xl font-bold text-red-600" data-testid="total-office-expenses">{formatNaira(totalAmount)}</p>
            </CardContent>
          </Card>
          {Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([cat, amt]) => (
            <Card key={cat} className="border-neutral-border shadow-card">
              <CardContent className="p-4">
                <p className="text-xs text-neutral-500">{ALLOWED_CATEGORIES[cat] || cat}</p>
                <p className="text-lg font-bold">{formatNaira(amt)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
            data-testid="filter-office-category"
          >
            <option value="">All Categories</option>
            {Object.entries(ALLOWED_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl text-sm h-10" data-testid="filter-office-date-from" />
            <span className="text-neutral-400">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl text-sm h-10" data-testid="filter-office-date-to" />
          </div>
          {(filterCategory || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterCategory(''); setDateFrom(''); setDateTo(''); }} className="text-neutral-500">
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card ref={formRef} className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{editExpense ? 'Edit Office Expense' : 'Record New Office Expense'}</CardTitle>
                <CardDescription>Capture day-to-day operational spending. Use the description to specify what was spent on.</CardDescription>
              </div>
              <button onClick={() => { setShowForm(false); setEditExpense(null); }} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Amount (Naira) *</Label>
                  <NairaInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="mt-1.5 rounded-xl" data-testid="office-form-amount" />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="office-form-category">
                    {Object.entries(ALLOWED_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="mt-1.5 rounded-xl" data-testid="office-form-date" />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                    <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="pos">POS</option><option value="online">Online</option>
                  </select>
                </div>
                <div><Label>Vendor / Payee</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Who received the money?" data-testid="office-form-vendor" /></div>
                <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Receipt / Invoice #" /></div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <Label>What was spent on? *</Label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="e.g., Office printer toner replacement"
                    className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm resize-none"
                    data-testid="office-form-description"
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditExpense(null); }} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="office-form-submit">
                    {submitting ? 'Saving...' : editExpense ? 'Update Expense' : 'Record Expense'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Expenses Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="office-expenses-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Description</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden lg:table-cell">Recorded By</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden lg:table-cell">Method</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={exp.id} className="border-b hover:bg-neutral-50 transition-colors" data-testid={`office-expense-row-${exp.id}`}>
                    <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{exp.expense_date}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[exp.category] || 'bg-neutral-100 text-neutral-800'}`}>{ALLOWED_CATEGORIES[exp.category] || exp.category}</span></td>
                    <td className="px-4 py-3 hidden md:table-cell text-neutral-700 max-w-xs">
                      <div className="truncate" title={exp.description}>{exp.description || '--'}</div>
                      {exp.vendor && <div className="text-xs text-neutral-500">to {exp.vendor}</div>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-neutral-600 text-xs">{exp.recorded_by_name || '--'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-neutral-600">{methodLabels[exp.payment_method] || exp.payment_method}</td>
                    <td className="px-4 py-3 text-right font-bold text-red-600 whitespace-nowrap">{formatNaira(exp.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit(exp) && (
                          <>
                            <button onClick={() => handleEdit(exp)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600" title="Edit" data-testid={`edit-office-expense-${exp.id}`}><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => setConfirm({ open: true, expense: exp })} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`delete-office-expense-${exp.id}`}><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No office expenses yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && setConfirm({ open: false, expense: null })}
        title="Delete this expense?"
        description={confirm.expense ? `${formatNaira(confirm.expense.amount)} for "${confirm.expense.description || ALLOWED_CATEGORIES[confirm.expense.category]}" will be permanently removed.` : ''}
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await boAPI.deleteExpense(confirm.expense.id);
            toast.success('Office expense deleted');
            setConfirm({ open: false, expense: null });
            fetchExpenses();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
          }
        }}
      />
    </BOLayout>
  );
};

export default BOOfficeExpenses;
